import { buildFeatures, parseDocumentationGeojson, type DocPoint } from './features'
import { buildImageIndex, type PhotoRef } from './imageIndex'

export type PointColumns = {
  numbers: Float64Array
  photos: Int32Array
  tracks: string[]
  imagePaths: string[]
  props: string[]
}

export const pointsFromGeojson = (bytes: Uint8Array, images: PhotoRef[]): DocPoint[] =>
  buildFeatures(parseDocumentationGeojson(new TextDecoder().decode(bytes)), buildImageIndex(images))

export const encodePoints = (points: DocPoint[], images: PhotoRef[]): PointColumns => {
  const slots = new Map(images.map((image, index) => [image, index]))
  const numbers = new Float64Array(points.length * 3)
  const photos = new Int32Array(points.length)
  points.forEach((point, index) => {
    numbers[index * 3] = point.lon
    numbers[index * 3 + 1] = point.lat
    numbers[index * 3 + 2] = point.time ?? Number.NaN
    photos[index] = point.image ? (slots.get(point.image) ?? -1) : -1
  })
  return {
    numbers,
    photos,
    tracks: points.map(point => point.track),
    imagePaths: points.map(point => point.imagePath),
    props: points.map(point => JSON.stringify(point.props())),
  }
}

export const columnTransfer = (columns: PointColumns): Transferable[] => [
  columns.numbers.buffer as ArrayBuffer,
  columns.photos.buffer as ArrayBuffer,
]

const lazyProps = (json: string) => {
  let parsed: Record<string, unknown> | null = null
  return () => {
    if (parsed) return parsed
    try {
      const value = JSON.parse(json) as unknown
      parsed = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
    } catch {
      parsed = {}
    }
    return parsed
  }
}

export const decodePoints = (columns: PointColumns, images: PhotoRef[]): DocPoint[] =>
  columns.tracks.map((track, index) => {
    const time = columns.numbers[index * 3 + 2]
    return {
      lon: columns.numbers[index * 3],
      lat: columns.numbers[index * 3 + 1],
      time: Number.isNaN(time) ? null : time,
      track,
      imagePath: columns.imagePaths[index],
      image: images[columns.photos[index]] ?? null,
      props: lazyProps(columns.props[index]),
    }
  })
